"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stretcherDefaultConfigByVehicle = exports.stretcherTransportReasonOrder = exports.stretcherPatientConditionOrder = exports.stretcherPatientSpecificNeedOrder = exports.stretcherVehicleOrder = exports.stretcherPatientFieldStateLabels = exports.stretcherTransportReasonLabels = exports.stretcherPatientConditionLabels = exports.stretcherPatientSpecificNeedLabels = exports.stretcherVehicleLabels = void 0;
var prisma_1 = require("@ambler/prisma");
/**
 * The stretcher-bearing referential: what each enum value is called, and the order the screens
 * show them in.
 *
 * It lives here rather than in `packages/api` because both sides need it — the API to seed a
 * facility's rows, the BO to offer a type the facility has not taken up yet (and which therefore
 * has no row carrying its label). Two copies would drift the day a wording changes.
 *
 * These are the *default* wordings. Once a row exists, the facility's own `label` wins: it is
 * renamable, and that is the whole point of the customization.
 */
exports.stretcherVehicleLabels = {
    WALKING: 'Accompagnement à pied',
    STRETCHER: 'Brancard',
    BARIATRIC_STRETCHER: 'Brancard bariatrique',
    MEDICAL_BED: 'Lit médicalisé',
    ALZHEIMER_BED: 'Lit Alzheimer',
    WHEELCHAIR: 'Fauteuil roulant',
};
exports.stretcherPatientSpecificNeedLabels = {
    WHEELCHAIR: 'Fauteuil roulant',
    PROTECTIVE_ISOLATION: 'Isolement protecteur',
    OXYGEN: 'Oxygène',
    VACUUM_MATTRESS: 'Matelas coquille',
    SYRINGE_PUMP: 'Pousse-seringue',
    INCUBATOR: 'Incubateur',
    MONITOR: 'Scope',
};
exports.stretcherPatientConditionLabels = {
    CONTAGIOUS: 'Contagieux',
    PAIN_OR_DISCOMFORT: 'Gênes ou douleurs',
    CONTACT_ISOLATION: 'Isolement contact',
    NEWBORN_OR_CHILD: 'Nouveau-né / Enfant',
    AGITATED: 'Patient agité',
    DISORIENTED: 'Patient désorienté',
    ON_INFUSION: 'Perfusé',
    PREMATURE: 'Prématuré',
    OVERWEIGHT: 'Surpoids (<130 kg)',
};
exports.stretcherTransportReasonLabels = {
    CONSULTATION: 'Consultation / Examen',
    IMAGING: 'Imagerie (IRM, Scanner, Radiographie, etc)',
    OPERATING_ROOM: 'Transfert au bloc opératoire',
    EMERGENCY: 'Transfert aux urgences',
    RETURN_TO_ROOM: 'Retour en chambre',
    ONCOLOGY_DIALYSIS: 'Chimiothérapie / Radiothérapie / Dialyse',
    DEPARTMENT_CHANGE: 'Changement de service',
    DAY_HOSPITAL: 'Transfert en HDJ',
    OTHER: 'Autre',
};
exports.stretcherPatientFieldStateLabels = {
    USED: 'Afficher',
    USED_AND_REQUIRED: 'Afficher et obligatoire',
    UNUSED: 'Cacher',
};
/*
 * The display orders, given as a rank per type rather than as a list: a list accepts any subset, so a
 * type added to the schema but not to its list would silently show on no screen, and could not be
 * offered to a facility. A `Record` does not compile until the new type is placed.
 */
/** Vehicles are not alphabetical anywhere: this is the order of the mockup, lightest first. */
var stretcherVehicleRanks = {
    WALKING: 0,
    STRETCHER: 1,
    BARIATRIC_STRETCHER: 2,
    MEDICAL_BED: 3,
    ALZHEIMER_BED: 4,
    WHEELCHAIR: 5,
};
exports.stretcherVehicleOrder = Object.values(prisma_1.$Enums.StretcherModVehicleType).sort(function (a, b) { return stretcherVehicleRanks[a] - stretcherVehicleRanks[b]; });
var stretcherPatientSpecificNeedRanks = {
    WHEELCHAIR: 0,
    PROTECTIVE_ISOLATION: 1,
    OXYGEN: 2,
    VACUUM_MATTRESS: 3,
    SYRINGE_PUMP: 4,
    INCUBATOR: 5,
    MONITOR: 6,
};
exports.stretcherPatientSpecificNeedOrder = Object.values(prisma_1.$Enums.StretcherModPatientSpecificNeedType).sort(function (a, b) { return stretcherPatientSpecificNeedRanks[a] - stretcherPatientSpecificNeedRanks[b]; });
var stretcherPatientConditionRanks = {
    CONTAGIOUS: 0,
    PAIN_OR_DISCOMFORT: 1,
    CONTACT_ISOLATION: 2,
    NEWBORN_OR_CHILD: 3,
    AGITATED: 4,
    DISORIENTED: 5,
    ON_INFUSION: 6,
    PREMATURE: 7,
    OVERWEIGHT: 8,
};
exports.stretcherPatientConditionOrder = Object.values(prisma_1.$Enums.StretcherModPatientConditionType).sort(function (a, b) { return stretcherPatientConditionRanks[a] - stretcherPatientConditionRanks[b]; });
var stretcherTransportReasonRanks = {
    CONSULTATION: 0,
    IMAGING: 1,
    OPERATING_ROOM: 2,
    EMERGENCY: 3,
    RETURN_TO_ROOM: 4,
    ONCOLOGY_DIALYSIS: 5,
    DEPARTMENT_CHANGE: 6,
    DAY_HOSPITAL: 7,
    OTHER: 8,
};
exports.stretcherTransportReasonOrder = Object.values(prisma_1.$Enums.StretcherModTransportReasonType).sort(function (a, b) { return stretcherTransportReasonRanks[a] - stretcherTransportReasonRanks[b]; });
/**
 * What each vehicle offers the day its facility turns the module on: the API lays its rows down from
 * it, and the BO resets a step to it. Every vehicle and every option gets a row; `isUsed` says which
 * ones are offered. The table is exhaustive, so a type added to the schema doesn't compile until it
 * is given a value here.
 */
exports.stretcherDefaultConfigByVehicle = {
    WALKING: {
        isUsed: true,
        patientSpecificNeeds: {
            WHEELCHAIR: true,
            PROTECTIVE_ISOLATION: true,
            OXYGEN: true,
            VACUUM_MATTRESS: false,
            SYRINGE_PUMP: false,
            INCUBATOR: false,
            MONITOR: false,
        },
        patientConditions: {
            CONTAGIOUS: true,
            PAIN_OR_DISCOMFORT: true,
            CONTACT_ISOLATION: true,
            NEWBORN_OR_CHILD: false,
            AGITATED: true,
            DISORIENTED: true,
            ON_INFUSION: true,
            PREMATURE: false,
            OVERWEIGHT: true,
        },
    },
    STRETCHER: {
        isUsed: true,
        patientSpecificNeeds: {
            WHEELCHAIR: true,
            PROTECTIVE_ISOLATION: true,
            OXYGEN: true,
            VACUUM_MATTRESS: true,
            SYRINGE_PUMP: true,
            INCUBATOR: true,
            MONITOR: true,
        },
        patientConditions: {
            CONTAGIOUS: true,
            PAIN_OR_DISCOMFORT: true,
            CONTACT_ISOLATION: true,
            NEWBORN_OR_CHILD: true,
            AGITATED: true,
            DISORIENTED: true,
            ON_INFUSION: true,
            PREMATURE: true,
            OVERWEIGHT: true,
        },
    },
    BARIATRIC_STRETCHER: {
        isUsed: true,
        patientSpecificNeeds: {
            WHEELCHAIR: true,
            PROTECTIVE_ISOLATION: true,
            OXYGEN: true,
            VACUUM_MATTRESS: true,
            SYRINGE_PUMP: true,
            INCUBATOR: true,
            MONITOR: true,
        },
        patientConditions: {
            CONTAGIOUS: true,
            PAIN_OR_DISCOMFORT: true,
            CONTACT_ISOLATION: true,
            NEWBORN_OR_CHILD: true,
            AGITATED: true,
            DISORIENTED: true,
            ON_INFUSION: true,
            PREMATURE: true,
            OVERWEIGHT: true,
        },
    },
    MEDICAL_BED: {
        isUsed: true,
        patientSpecificNeeds: {
            WHEELCHAIR: true,
            PROTECTIVE_ISOLATION: true,
            OXYGEN: true,
            VACUUM_MATTRESS: true,
            SYRINGE_PUMP: true,
            INCUBATOR: true,
            MONITOR: true,
        },
        patientConditions: {
            CONTAGIOUS: true,
            PAIN_OR_DISCOMFORT: true,
            CONTACT_ISOLATION: true,
            NEWBORN_OR_CHILD: true,
            AGITATED: true,
            DISORIENTED: true,
            ON_INFUSION: true,
            PREMATURE: true,
            OVERWEIGHT: true,
        },
    },
    ALZHEIMER_BED: {
        isUsed: true,
        patientSpecificNeeds: {
            WHEELCHAIR: false,
            PROTECTIVE_ISOLATION: false,
            OXYGEN: false,
            VACUUM_MATTRESS: false,
            SYRINGE_PUMP: false,
            INCUBATOR: false,
            MONITOR: false,
        },
        patientConditions: {
            CONTAGIOUS: false,
            PAIN_OR_DISCOMFORT: false,
            CONTACT_ISOLATION: false,
            NEWBORN_OR_CHILD: false,
            AGITATED: false,
            DISORIENTED: false,
            ON_INFUSION: false,
            PREMATURE: false,
            OVERWEIGHT: false,
        },
    },
    WHEELCHAIR: {
        isUsed: true,
        patientSpecificNeeds: {
            WHEELCHAIR: true,
            PROTECTIVE_ISOLATION: true,
            OXYGEN: true,
            VACUUM_MATTRESS: false,
            SYRINGE_PUMP: true,
            INCUBATOR: false,
            MONITOR: true,
        },
        patientConditions: {
            CONTAGIOUS: true,
            PAIN_OR_DISCOMFORT: true,
            CONTACT_ISOLATION: true,
            NEWBORN_OR_CHILD: true,
            AGITATED: true,
            DISORIENTED: true,
            ON_INFUSION: true,
            PREMATURE: false,
            OVERWEIGHT: true,
        },
    },
};
//# sourceMappingURL=stretcher.js.map